import { useMemo } from 'react';
import { Skeleton } from 'antd';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { formatMinutesCompact } from './ContentActivityUtils';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const formatHourLabel = (hour, is24hoursformat) => {
  if (is24hoursformat) return `${String(hour).padStart(2, '0')}:00`;
  if (hour === 0) return '12 AM';
  if (hour === 12) return '12 PM';
  if (hour < 12) return `${hour} AM`;
  return `${hour - 12} PM`;
};

const ContentActivityCharts = (props) => {
  const { daily, hourly, channels, loading, loginUserTimeformat } = props;
  const trans = useSelector(translator);
  const is24hoursformat = loginUserTimeformat?.is24hoursformat;

  const channelNameByKey = useMemo(() => {
    const map = {};
    (channels || []).forEach((channel) => {
      map[channel.key] = channel.channelName || channel.channelId || channel.key;
    });
    return map;
  }, [channels]);

  const dailyHasData = useMemo(() => (daily || []).some((day) => day.totalMs > 0), [daily]);

  const hourlyHasData = useMemo(
    () => (hourly || []).some((bucket) => bucket.watchMs > 0),
    [hourly]
  );

  const dailyChart = useMemo(() => {
    const labels = (daily || []).map((day) => day.label);
    const datasets = (channels || []).map((channel) => ({
      label: channelNameByKey[channel.key],
      data: (daily || []).map((day) => (day.byChannel[channel.key] || 0) / 60000),
      backgroundColor: channel.color,
      stack: 'watch',
    }));
    return { labels, datasets };
  }, [daily, channels, channelNameByKey]);

  const dailyOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        // The chartjs-plugin-datalabels plugin is registered globally (see
        // WellnessHistoryInsightChart.jsx) and applies to every Chart.js chart
        // by default, so it must be explicitly disabled here — otherwise it
        // renders the raw, unrounded minute value (e.g. "181.9623332233332")
        // permanently on every bar segment. Values are only needed on hover,
        // which the tooltip below already provides, pre-rounded.
        datalabels: { display: false },
        tooltip: {
          filter: (tooltipItem) => tooltipItem.parsed.y > 0,
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatMinutesCompact(context.parsed.y)}`,
            afterBody: (items) => {
              const count = daily?.[items?.[0]?.dataIndex]?.otherEngagementCount || 0;
              return count > 0 ? [`${trans('CONTENT_OTHER_ENGAGEMENTS')}: ${count}`] : [];
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            font: { size: 11 },
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            callback: (value) => formatMinutesCompact(value),
          },
          title: { display: true, text: trans('MINUTES') || 'min' },
        },
      },
    }),
    [trans, daily]
  );

  const hourlyChart = useMemo(() => {
    const labels = (hourly || []).map((bucket) => formatHourLabel(bucket.hour, is24hoursformat));
    const datasets = (channels || []).map((channel) => ({
      label: channelNameByKey[channel.key],
      data: (hourly || []).map((bucket) => (bucket.byChannel?.[channel.key] || 0) / 60000),
      backgroundColor: channel.color,
      stack: 'watch',
    }));
    return { labels, datasets };
  }, [hourly, channels, channelNameByKey, is24hoursformat]);

  const hourlyOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'bottom' },
        // See note in dailyOptions above — must explicitly opt out of the
        // globally-registered datalabels plugin.
        datalabels: { display: false },
        tooltip: {
          filter: (tooltipItem) => tooltipItem.parsed.y > 0,
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${formatMinutesCompact(context.parsed.y)}`,
            afterBody: (items) => {
              const count = hourly?.[items?.[0]?.dataIndex]?.otherEngagementCount || 0;
              return count > 0 ? [`${trans('CONTENT_OTHER_ENGAGEMENTS')}: ${count}`] : [];
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            font: { size: 11 },
          },
        },
        y: {
          stacked: true,
          grid: {
            display: false,
          },
          ticks: {
            callback: (value) => formatMinutesCompact(value),
          },
          title: { display: true, text: trans('MINUTES') || 'min' },
        },
      },
    }),
    [trans, hourly]
  );

  const renderEmpty = () => (
    <div className="contentactivity-chart-empty">{trans('NO_CONTENT_ACTIVITY')}</div>
  );

  return (
    <div className="contentactivity-charts">
      <div className="chart-container">
        <div className="chart-title">{trans('DAILY_WATCH_TIME')}</div>
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : dailyHasData ? (
          <>
            <div className="chart-canvas-wrapper">
              <Bar data={dailyChart} options={dailyOptions} />
            </div>
          </>
        ) : (
          renderEmpty()
        )}
      </div>
      <div className="chart-container">
        <div className="chart-title">{trans('TIME_OF_DAY')}</div>
        {loading ? (
          <Skeleton active paragraph={{ rows: 4 }} />
        ) : hourlyHasData ? (
          <>
            <div className="chart-canvas-wrapper">
              <Bar data={hourlyChart} options={hourlyOptions} />
            </div>
          </>
        ) : (
          renderEmpty()
        )}
      </div>
    </div>
  );
};

ContentActivityCharts.propTypes = {
  daily: PropTypes.arrayOf(PropTypes.object).isRequired,
  hourly: PropTypes.arrayOf(PropTypes.object).isRequired,
  channels: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  loginUserTimeformat: PropTypes.object,
};

ContentActivityCharts.defaultProps = {
  channels: [],
};

export default ContentActivityCharts;
